import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { useColor4 } from '../hooks/useColor4';

/**
 * Theme wrapper around the Engrain Sightmap embed.
 * Drop-shadow, height cap, and the click-to-interact overlay live here so
 * the block embed stays untouched. The iframe would otherwise capture
 * every scroll and touch that lands on it. After the first click, the
 * overlay stays off for the rest of the visit.
 */
export function InteractiveMapSection() {
  const mapSlot = useBlockSlot('interactivemapsection.map_block', {
    defaultValue: 'engrain-sitemap',
  });
  const [isInteractive, setIsInteractive] = useState(false);
  const color4 = useColor4();

  return (
    <section className="ims-section">
      <div className="ims-shadow">
        <div className="ims-frame">
          <EditableToken
            path="interactivemapsection.map_block"
            as="div"
            className="ims-block"
          >
            {mapSlot ? (
              <SectionContextProvider sectionId={mapSlot.sectionId}>
                <mapSlot.BlockComponent sectionId={mapSlot.sectionId} />
              </SectionContextProvider>
            ) : (
              <div className="ims-empty">No block selected</div>
            )}
          </EditableToken>
          {mapSlot && !isInteractive ? (
            <button
              type="button"
              className="ims-overlay group/cta"
              onClick={() => setIsInteractive(true)}
              aria-label="Click to interact with the map"
            >
              <span className="ims-overlay-cta" style={color4?.primaryOverride}>
                <span className="relative z-10">Click to interact</span>
                <svg
                  className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </span>
            </button>
          ) : null}
        </div>
      </div>

      <style>{`
        .ims-section {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          box-sizing: border-box;
          background: var(--vibe-color-background, #ffffff);
          padding: 2.5rem 1.25rem;
          --ims-map-height: min(78vh, 720px);
        }
        @media (min-width: 768px) {
          .ims-section {
            padding: 3rem 1.5rem;
            --ims-map-height: min(82vh, 900px);
          }
        }
        @media (min-width: 1200px) {
          .ims-section { --ims-map-height: min(88vh, 980px); }
        }

        /* Shadow sits on the wrapper, not the block, so it is visible on
           all four sides of a full-bleed embed without clipping the iframe. */
        .ims-shadow {
          max-width: min(80rem, 100%);
          min-width: 0;
          margin: 0 auto;
          box-shadow:
            0 -14px 40px rgba(0, 0, 0, 0.12),
            0 22px 56px rgba(0, 0, 0, 0.18),
            0 6px 18px rgba(0, 0, 0, 0.08);
        }

        .ims-frame {
          position: relative;
          height: var(--ims-map-height);
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
          overscroll-behavior-x: none;
          touch-action: pan-y;
          background: #ffffff;
        }

        .ims-block,
        .ims-block :is(section, iframe, .rrac_interactive_leasing_view) {
          display: block;
          box-sizing: border-box;
          width: 100%;
          max-width: 100% !important;
          min-width: 0 !important;
          height: 100% !important;
          max-height: 100% !important;
          min-height: 0 !important;
        }

        .ims-empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }

        /* Section-owned overlay. Sits on the frame, never on the block, so
           the embed keeps its own markup and the iframe cannot steal scroll
           until the visitor opts in. */
        .ims-overlay {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
          touch-action: pan-y;
        }
        .ims-overlay:hover,
        .ims-overlay:focus-visible {
          background: rgba(0, 0, 0, 0.18);
        }
        .ims-overlay:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }
        .ims-overlay-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.625rem;
          pointer-events: none;
          position: relative;
          overflow: hidden;
          padding: 1rem 2.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary);
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
          transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
        }
        .ims-overlay-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .ims-overlay:hover .ims-overlay-cta,
        .ims-overlay:focus-visible .ims-overlay-cta {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .ims-overlay:hover .ims-overlay-cta::before,
        .ims-overlay:focus-visible .ims-overlay-cta::before {
          left: 100%;
        }
        .ims-overlay:active .ims-overlay-cta {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        @media (prefers-reduced-motion: reduce) {
          .ims-overlay-cta,
          .ims-overlay-cta::before { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.map_block': {
      label: 'Map Block',
      type: 'block',
      defaultValue: 'engrain-sitemap',
      acceptedBlockCategories: ['Integrations', 'Embeds'],
    },
  },
  slotGroups: {},
  collectionBindings: {},
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InteractiveMapSection;
